import type { Recipe } from '../types';

// The savory half of the breakfast pair. Cottage cheese is blended completely
// smooth and then baked, so there is no curd left to find — worth saying out
// loud given the texture thing, and the cream cheese swap is in the notes.

export const cottageCheeseEggBites: Recipe = {
  slug: 'cottage-cheese-egg-bites',
  title: 'Cottage Cheese Egg Bites',
  meal: 'breakfast',
  category: 'meal',
  types: ['breakfast', 'bake'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'make-ahead', 'high-protein', 'blender'],
  yield: '24 bites',
  activeTime: '15 min',
  totalTime: '45 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    'The Starbucks ones, made two dozen at a time for about a tenth of the price. Blender, muffin tins, done — and unlike most egg-muffin recipes these do not deflate into rubbery discs, because of the cottage cheese.',

  flavorNotes:
    'Velvety and custardy rather than firm and eggy — that is the difference between these and a standard egg muffin, and it is worth knowing what you are aiming for. The cottage cheese gets blended perfectly smooth and then baked, so there is no curd, no graininess and nothing to find with your teeth; it reads as richness, not as cottage cheese. Savory, salty from the bacon and cheddar, mild enough that nobody negotiates over breakfast. If you want to be certain about the texture, swap in an equal amount of softened cream cheese and lose almost nothing.',

  ingredients: [
    {
      id: 'eggs',
      name: 'Large eggs',
      total: '12',
      uses: [{ step: 2, amount: '12' }],
    },
    {
      id: 'cottage-cheese',
      name: 'Full-fat cottage cheese',
      total: '1½ cups',
      prep: 'full-fat only — low-fat weeps water and the bites collapse',
      uses: [{ step: 2, amount: '1½ cups' }],
    },
    {
      id: 'cheddar',
      name: 'Tillamook sharp cheddar',
      total: '1½ cups',
      prep: 'shredded off the block',
      uses: [
        { step: 3, amount: '1 cup', note: 'stirred through the mix' },
        { step: 4, amount: '½ cup', note: 'sprinkled on top before baking' },
      ],
    },
    {
      id: 'bacon',
      name: 'Bacon',
      total: '8 slices',
      prep: 'cooked crisp and chopped',
      uses: [{ step: 3, amount: '8 slices' }],
    },
    {
      id: 'green-onions',
      name: 'Green onions',
      total: '4',
      prep: 'sliced thin',
      uses: [{ step: 3, amount: '4' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'pepper',
      name: 'Black pepper',
      total: '½ tsp',
      uses: [{ step: 2, amount: '½ tsp' }],
    },
    {
      id: 'hot-sauce',
      name: 'Hot sauce',
      total: 'a few dashes',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'Disappears completely into the custard. Adds savor and depth, not heat.',
      uses: [{ step: 2, amount: 'a few dashes', note: 'disappears — adds savor, not heat' }],
    },
    {
      id: 'spinach',
      name: 'Fresh spinach',
      total: '2 cups',
      prep: 'chopped fine',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'Wilts down to almost nothing. The easiest vegetable in the book to hide in a breakfast.',
      uses: [{ step: 3, amount: '2 cups', note: 'wilts down to almost nothing' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Heat the oven low and grease the tins properly',
      time: '5 min',
      critical: true,
      ingredientIds: [],
      body:
        'Oven to 325°F — low, and it matters. Grease two 12-cup muffin tins far more thoroughly than feels necessary, getting into the corner where the wall meets the bottom. Silicone cups are genuinely better here if you own them.',
      tip:
        'Egg sticks to metal like glue. This is the step people regret skipping, usually while chiseling breakfast out of a pan with a butter knife.',
    },
    {
      n: 2,
      title: 'Blend the custard',
      time: '2 min',
      critical: true,
      ingredientIds: ['eggs', 'cottage-cheese', 'salt', 'pepper', 'hot-sauce'],
      body:
        'Into the Ninja: the {12|eggs} eggs, {1½ cups|cottage-cheese} of full-fat cottage cheese, {1 tsp|salt} of salt, {½ tsp|pepper} of pepper and the hot sauce if you want it. Blend on medium for about 30 seconds — long enough that it is perfectly smooth with no white flecks left, but not so long that it turns foamy.',
      tip:
        'Medium speed, not high. Whipping air into the custard makes them rise up like soufflés in the oven and then sink into wrinkled little craters on the counter. Smooth, not frothy.',
    },
    {
      n: 3,
      title: 'Stir in the solids by hand',
      time: '3 min',
      ingredientIds: ['cheddar', 'bacon', 'green-onions', 'spinach'],
      body:
        'Pour the custard into a bowl or big measuring cup and stir in {1 cup|cheddar} of the shredded cheddar, the chopped bacon, and the green onions. The spinach goes in here too if you are using it. Everything solid gets stirred in, never blended — blend the bacon and you get grey custard.',
    },
    {
      n: 4,
      title: 'Fill and top',
      time: '4 min',
      ingredientIds: ['cheddar'],
      body:
        'Pour into the cups about three-quarters full, stirring the bowl between pours so the bacon and cheese do not all settle into the last six. Scatter the remaining {½ cup|cheddar} of cheddar over the tops.',
    },
    {
      n: 5,
      title: 'Bake until barely set',
      time: '22–25 min',
      critical: true,
      ingredientIds: [],
      body:
        'Bake at 325°F for 22 to 25 minutes. They are done when the tops are set and no longer wet, and the centers still have the faintest wobble to them. They will puff in the oven and settle a little as they cool — that is normal and not a mistake.',
      tip:
        'Overbaked egg bites go from custardy to spongy in about three minutes. Pull them the moment the surface stops looking wet.',
    },
    {
      n: 6,
      title: 'Cool, then release',
      time: '10 min',
      ingredientIds: [],
      body:
        'Let them sit in the tins 10 minutes before you try to get them out — they firm up as they cool and shrink back from the sides on their own. Run a thin knife around any that resist. Cool completely on a rack before they go anywhere near the freezer.',
    },
  ],

  freezing:
    'Freeze in a single layer on a sheet pan for an hour first so they do not fuse, then into a gallon bag for up to 3 months. Three bites is a breakfast. In the fridge rather than the freezer they hold about four days.',

  reheat: [
    {
      method: 'Microwave from frozen',
      best: true,
      detail:
        'Wrap in a paper towel and microwave at 50% power for about 60 to 90 seconds. The paper towel absorbs the water that comes off them, and half power keeps them custardy instead of turning them into little rubber pucks. Full power ruins these.',
    },
    {
      method: 'Air fryer',
      detail:
        '350°F for about 5 minutes from thawed. Best texture of any method — the outside firms up again and the middle stays soft.',
    },
    {
      method: 'Oven',
      detail:
        '300°F for 10 to 12 minutes from thawed, covered loosely with foil. Worth it when you are heating a whole batch for several people at once.',
    },
  ],

  glossary: [
    {
      term: 'custard',
      plain:
        'Eggs and dairy cooked gently together until they set into something soft and sliceable. Low, slow heat keeps it silky; high heat makes the egg proteins squeeze together and turn it rubbery and weeping. The 325°F oven is doing exactly this job.',
    },
    {
      term: 'wobble',
      plain:
        'Give the pan a gentle nudge. The centers should move very slightly, like set jello, while the edges stay put. That means carryover heat will finish them perfectly as they cool. No wobble at all means you have gone too far.',
    },
    {
      term: 'full-fat',
      plain:
        'Four percent milkfat cottage cheese, not one or two percent. The lower-fat versions carry more water, and that water leaves during baking and takes the structure with it — the bites sink and go watery in the middle.',
    },
  ],

  inspiration: [
    { label: "Natasha's Kitchen — cottage cheese egg bites", url: 'https://natashaskitchen.com/cottage-cheese-egg-bites/' },
    { label: 'The Kitchn — Starbucks egg bites copycat', url: 'https://www.thekitchn.com/egg-bites-starbucks-23761698' },
  ],

  whereWeVeered:
    'Starbucks uses a sous-vide bath and most copycat recipes ask for a roasting pan full of hot water. We skipped the water bath entirely — full-fat cottage cheese brings enough moisture that a 325°F oven gets you within a hair of the same texture, and nobody is sliding a pan of boiling water into an oven at six in the morning. We also doubled it to 24, moved half the cheddar to the top for browning, and specified 50% power on the reheat, which is the single thing that separates a good frozen egg bite from a rubbery one.',
};
